import { useOidcAccessToken } from '@axa-fr/react-oidc';
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type CustomClaims = {
  system: string;
  reportNameAlias: string;
  templateVersion: string;
  permissionType: string;
};

type CustomClaimsContextProps = {
  customClaims: CustomClaims;
};

const CustomClaimsContext = createContext<CustomClaimsContextProps>({
  customClaims: {
    system: '',
    reportNameAlias: '',
    templateVersion: '',
    permissionType: '',
  },
});

export function CustomClaimsProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const { accessToken } = useOidcAccessToken();
  const [isReady, setIsReady] = useState(false);
  const [claims, setClaims] = useState<CustomClaims>({
    system: '',
    reportNameAlias: '',
    templateVersion: '',
    permissionType: '',
  });

  const value = useMemo(() => ({ customClaims: claims }), [claims]);

  useEffect(() => {
    if (!accessToken) {
      setIsReady(false);
      return;
    }

    if (isReady) return;

    const parts = accessToken.split('.');
    const payload = JSON.parse(atob(parts[1]));

    setClaims({
      system: payload.system,
      reportNameAlias: payload.reportNameAlias,
      templateVersion: payload.templateVersion,
      permissionType: payload.permissionType,
    });

    setIsReady(true);
  }, [accessToken]);

  return isReady ? (
    <CustomClaimsContext.Provider value={value}>
      {children}
    </CustomClaimsContext.Provider>
  ) : null;
}

export const useCustomClaims = () => useContext(CustomClaimsContext);
